import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { Checkbox, getLoggedIn, Translation as T, useTranslation } from "@awe-web/shared/lib";
import { ScreenSection } from "../../components/screen.js";
import { TypeArea } from "../../components/layout.js";
import { Row, Col } from "../../components/grid.js";
import { Button, ScrollToError } from "../../components/form.js";
import {
  FormikSingleChoiceOptions,
  FormikMultiChoiceOptions
} from "../../components/option.js";
import {
  getLtrMileageOptions,
  getLtrEquipmentOptions,
  newGetLtrSelectExtrasFormValuesToPrices,
  newGetLtrSelectExtrasFormValuesToTotalPrice
} from "../store.js";
import { LtrMonthlyTotalPrice } from "./price";

import "./ltr_select_extras_form.scss";

const SELECT_MILEAGE = "ALG0000032";
const SELECT_DEDUCTIBLE = "FSD0000249";
const SELECT_EQUIPMENT = "FSD0000252";
const CONTINUE = "FSD0000135";
const MORE_INFOS_MILEAGE_HEADING = "ALG0000058";
const MORE_INFOS_MILEAGE_CONTENT = "ALG0000059";
const MORE_INFOS_DEDUCTIBLE_HEADING = "ALG0000239";
const MORE_INFOS_DEDUCTIBLE_CONTENT = "ALG0000240";

export default function LTR_SelectExtrasForm({
  reservationPath,
  deductibleOptions,
  totalPriceHintCode,
  mileageOptionChangedMessage,
  equipmentOptionsChangedMessage,
  values,
  handleSubmit,
  onGuestCheck
}) {

  const { translate } = useTranslation();

  const mileageOptions = useSelector((state) => getLtrMileageOptions(state));
  const equipmentOptions = useSelector((state) => getLtrEquipmentOptions(state));

  const getPrices = useSelector((state) => newGetLtrSelectExtrasFormValuesToPrices(state));
  const getTotalPrice = useSelector((state) => newGetLtrSelectExtrasFormValuesToTotalPrice(state));

  const isLoggedIn = useSelector((state) => getLoggedIn(state));

  const lineup = useMemo(() => getPrices(values), [getPrices, values]);
  const formattedPrice = useMemo(() => getTotalPrice(values), [getTotalPrice, values]);

  return (
    <form onSubmit={handleSubmit}>
      <ScrollToError />

      <TypeArea className="ltr-select-extras-form__type-area">
        <Row alignBottom className="ltr-select-extras-form__row">
          <Col tablet={{ cols: 4 }} desktop={{ offset: 1, cols: 5 }}>
            <ScreenSection className="ltr-select-extras-form__extras" light>
              <FormikSingleChoiceOptions
                name="mileage"
                showPrices
                className="ltr-select-extras-form__mileage-options"
                title={translate(SELECT_MILEAGE)}
                notice={mileageOptionChangedMessage}
                options={mileageOptions.toJS()}
                moreInfos={{
                  heading: translate(MORE_INFOS_MILEAGE_HEADING),
                  content: translate(MORE_INFOS_MILEAGE_CONTENT)
                }}
              />
              <FormikSingleChoiceOptions
                name="deductible"
                showPrices
                className="ltr-select-extras-form__deductible-options"
                title={translate(SELECT_DEDUCTIBLE)}
                options={deductibleOptions}
                moreInfos={{
                  heading: translate(MORE_INFOS_DEDUCTIBLE_HEADING),
                  content: translate(MORE_INFOS_DEDUCTIBLE_CONTENT)
                }}
              />
              <FormikMultiChoiceOptions
                name="equipment"
                showPrices
                title={translate(SELECT_EQUIPMENT)}
                notice={equipmentOptionsChangedMessage}
                options={equipmentOptions.toJS()}
              />
            </ScreenSection>
          </Col>

          <Col tablet={{ cols: 4 }} desktop={{ offset: 1, cols: 4 }}>
            <LtrMonthlyTotalPrice
              reservationPath={reservationPath}
              descriptionHint={translate(totalPriceHintCode)}
              lineup={lineup}
              formattedPrice={formattedPrice}
            />
          </Col>
        </Row>

        <Row className="ltr-select-extras-form__row">
          <Col tablet={{ offset: 4, cols: 4 }} desktop={{ offset: 7, cols: 4 }}>
            <ScreenSection className="ltr-select-extras-form__actions" dark>
              <Button primary continue submit><T code={CONTINUE} /></Button>
              {!isLoggedIn && onGuestCheck !== undefined && <label className="labeled-checkbox str-select-extras-form__as_guest">
                <Checkbox
                  onChange={onGuestCheck}
                  className="option-with-text-input__checkbox labeled-checkbox__input "
                />
                <T code={"FSD0000454"} />
              </label>
              }
            </ScreenSection>
          </Col>
        </Row>
      </TypeArea>
    </form>
  );
}

LTR_SelectExtrasForm.propTypes = {
  reservationPath: PropTypes.oneOf(["B2C", "B2B"]),
  deductibleOptions: FormikSingleChoiceOptions.propTypes.options,
  totalPriceHintCode: PropTypes.string,
  mileageOptionChangedMessage: FormikSingleChoiceOptions.propTypes.notice,
  equipmentOptionsChangedMessage: FormikMultiChoiceOptions.propTypes.notice,
  values: PropTypes.shape({
    mileage: PropTypes.number,
    deductible: PropTypes.number,
    equipment: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ]))
  }),
  handleSubmit: PropTypes.func.isRequired
};
